import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsEnvelope, BsMicrosoft } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Panel from '#globalComponents/Overlay/Panel';

import { reauthorizeOAuthAccount } from '../../../api/emailAccountsService';

const PROVIDER_CONFIGS = {
	outlook: { name: 'Outlook / Office 365', icon: BsMicrosoft },
};

const OAuthInfoStep = ({ account, onReauthorizeSuccess, onClose }) => {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);
	const [isConnecting, setIsConnecting] = useState(false);

	const config = PROVIDER_CONFIGS[account.provider] || { name: account.provider, icon: BsEnvelope };
	const ProviderIcon = config.icon;

	// Check if token is expired or expiring soon
	const isTokenExpired = () => {
		if (!account.tokenExpiresAt) return false;
		const expiresAt = new Date(account.tokenExpiresAt);
		const now = new Date();
		const fiveMinutes = 5 * 60 * 1000;
		return expiresAt.getTime() - now.getTime() < fiveMinutes;
	};

	const tokenExpired = isTokenExpired();

	const handleReauthorize = async () => {
		setIsLoading(true);
		setError(null);
		setIsConnecting(true);

		try {
			// Get authorization URL from backend
			const { authUrl } = await reauthorizeOAuthAccount(account.id);

			// Open OAuth flow in popup window
			const width = 600;
			const height = 700;
			const left = window.screen.width / 2 - width / 2;
			const top = window.screen.height / 2 - height / 2;

			const popup = window.open(
				authUrl,
				'OAuth Reauthorization',
				`width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`,
			);

			if (!popup) {
				throw new Error('Popup wurde blockiert. Bitte erlauben Sie Popups für diese Seite.');
			}

			// Poll popup window for completion
			const checkInterval = setInterval(() => {
				if (popup.closed) {
					clearInterval(checkInterval);
					setIsLoading(false);
					setIsConnecting(false);

					// Check if OAuth was successful by querying the backend
					// The backend should have updated the account if successful
					// We'll let the parent component handle refetching and closing
					onReauthorizeSuccess();
				}
			}, 500);

			// Listen for message from popup (alternative to polling)
			const handleMessage = (event) => {
				// Verify origin for security
				if (event.origin !== window.location.origin) {
					return;
				}

				if (event.data.type === 'oauth-success') {
					clearInterval(checkInterval);
					popup.close();
					setIsLoading(false);
					setIsConnecting(false);
					window.removeEventListener('message', handleMessage);
					onReauthorizeSuccess();
				} else if (event.data.type === 'oauth-error') {
					clearInterval(checkInterval);
					popup.close();
					setIsLoading(false);
					setIsConnecting(false);
					setError(event.data.error || 'Fehler beim Erneuern der Autorisierung');
					window.removeEventListener('message', handleMessage);
				}
			};

			window.addEventListener('message', handleMessage);

			// Timeout after 5 minutes
			setTimeout(
				() => {
					if (!popup.closed) {
						clearInterval(checkInterval);
						popup.close();
						setIsLoading(false);
						setIsConnecting(false);
						setError('OAuth-Timeout: Bitte versuchen Sie es erneut');
						window.removeEventListener('message', handleMessage);
					}
				},
				5 * 60 * 1000,
			);
		} catch (err) {
			setError(err.message || 'Fehler beim Starten der Reauthorisierung');
			setIsLoading(false);
			setIsConnecting(false);
		}
	};

	return (
		<>
			<Panel.Header subtitle="Verwalten Sie Ihre OAuth-Autorisierung" onClose={onClose}>
				OAuth-Konto Details
			</Panel.Header>

			<Panel.Body>
				<Card className="flex flex-col items-center gap-4 p-6 text-center">
					<div className="flex h-9 w-9 items-center justify-center rounded-control bg-primary-100 text-primary-600">
						<ProviderIcon className="h-4 w-4" />
					</div>

					<div>
						<h3 className="text-sm font-semibold text-text">{account.emailAddress}</h3>
						<p className="mt-0.5 text-sm text-bgBase-800">Provider: {config.name}</p>
					</div>

					{tokenExpired ? (
						<Alert tone="warning" title="Token abgelaufen" className="w-full text-left">
							Ihr OAuth-Token ist abgelaufen oder läuft bald ab. Bitte reauthorisieren Sie Ihr Konto, um
							es weiterhin nutzen zu können.
						</Alert>
					) : (
						<Alert tone="success" title="Token gültig" className="w-full text-left">
							Ihr OAuth-Token ist gültig. Sie können Ihr Konto jederzeit reauthorisieren, falls Sie
							Probleme haben.
						</Alert>
					)}

					{!isConnecting && !error && (
						<div>
							<p className="text-sm font-semibold text-text">
								Klicken Sie auf &quot;Reauthorisieren&quot;, um Ihr {config.name}-Konto erneut zu
								autorisieren
							</p>
							<p className="mt-1 text-xs text-bgBase-800">
								Sie werden zu {config.name} weitergeleitet, um die Berechtigung zu erteilen
							</p>
						</div>
					)}

					{isConnecting && (
						<div>
							<p className="text-sm font-semibold text-text">Warte auf Autorisierung …</p>
							<p className="mt-1 text-xs text-bgBase-800">
								Bitte schließen Sie den Autorisierungsprozess im Popup-Fenster ab
							</p>
						</div>
					)}

					{error && (
						<Alert tone="warning" title="Reauthorisierung fehlgeschlagen" className="w-full text-left">
							{error}
						</Alert>
					)}

					<Button
						variant={tokenExpired ? 'danger' : 'solid'}
						size="md"
						onClick={handleReauthorize}
						loading={isLoading}
					>
						Reauthorisieren
					</Button>
				</Card>
			</Panel.Body>
		</>
	);
};

OAuthInfoStep.propTypes = {
	account: PropTypes.shape({
		id: PropTypes.string.isRequired,
		emailAddress: PropTypes.string.isRequired,
		provider: PropTypes.string.isRequired,
		authStrategy: PropTypes.string.isRequired,
		tokenExpiresAt: PropTypes.string,
	}).isRequired,
	onReauthorizeSuccess: PropTypes.func.isRequired,
	onClose: PropTypes.func,
};

export default OAuthInfoStep;
